import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatDate, type ExportStatus, type Me, type Summary } from '../api.ts';
import { Icon } from '../components/Icon.tsx';

/**
 * Выгрузка коллекции (ТЗ, раздел 10).
 *
 * Это не вспомогательная функция, а главный механизм сохранности: пока
 * приложение не умеет выгружать коллекцию целиком в читаемый без него вид,
 * оно считается непригодным к использованию.
 */
export function ExportScreen({ me }: { me: Me }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<ExportStatus | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.exportStatus().then(setStatus);
    void api.summary().then(setSummary);
  }, []);

  async function download() {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/export/archive', { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Не удалось собрать архив (${response.status})`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `collection-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setStatus(await api.exportStatus());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось выгрузить');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">Выгрузка</h2>
        <div className="spacer" />
        <span style={{ width: 20 }} />
      </div>

      <div className="scroll">
        <p className="small muted" style={{ marginTop: 'var(--space-4)' }}>
          Архив самодостаточен: таблица для Excel, полные данные в JSON и все
          снимки, названные по инвентарным номерам. Человек, открывший его
          через десять лет без этого приложения, поймёт содержимое.
        </p>

        <dl className="rows">
          <div className="row">
            <dt>Образцов</dt>
            <dd>{summary?.specimens ?? '—'}</dd>
          </div>
          <div className="row">
            <dt>Последняя выгрузка</dt>
            <dd>
              {status?.last_export_at ? (
                <>
                  {formatDate(status.last_export_at.slice(0, 10))}
                  {status.days_since !== null && (
                    <div className="hint">{status.days_since} дней назад</div>
                  )}
                </>
              ) : (
                <span className="muted">не делалась</span>
              )}
            </dd>
          </div>
        </dl>

        {status?.remind && (
          <div className="notice notice--warn" style={{ marginTop: 'var(--space-4)' }}>
            <Icon name="alert" className="icon icon-sm" />
            <span>
              {status.last_export_at
                ? `Выгрузку не делали больше ${status.remind_after_days} дней.`
                : 'Выгрузка ещё ни разу не делалась.'}{' '}
              Скачайте архив и положите его туда, где он переживёт этот сервер.
            </span>
          </div>
        )}

        {/* Режим «без цен» вырезает суммы и из архива (ТЗ D4). Молчать об
            этом нельзя: человек, выгружающий коллекцию для страховой,
            иначе узнает о пропаже, когда архив уже уедет. */}
        {me.hide_prices && (
          <div className="notice notice--warn" style={{ marginTop: 'var(--space-4)' }}>
            <Icon name="alert" className="icon icon-sm" />
            <span>
              Включён режим «без цен»: цены покупки и оценки в архив не попадут.
              В приложении они целы. Если архив нужен вместе с суммами, выключите
              режим в настройках.
            </span>
          </div>
        )}

        {error && (
          <div className="notice notice--danger" style={{ marginTop: 'var(--space-4)' }}>
            {error}
          </div>
        )}

        <button
          className="btn btn--full"
          style={{ marginTop: 'var(--space-4)' }}
          onClick={() => {
            // PDF собирается на сервере и приходит файлом — открываем
            // прямой ссылкой, чтобы не держать его в памяти вкладки.
            window.location.href = '/api/export/catalogue.pdf';
          }}
        >
          <Icon name="printer" className="icon icon-sm" /> PDF-каталог: по странице на образец
        </button>
        <div className="hint centered">
          Для страховой и для себя: крупное фото, все поля, история оценок
        </div>

        <button
          className="btn btn--primary btn--full"
          style={{ marginTop: 'var(--space-5)' }}
          disabled={busy}
          onClick={download}
        >
          <Icon name="download" className="icon icon-sm" />
          {busy ? 'Собираем архив…' : 'Скачать архив'}
        </button>
        {busy && (
          <div className="hint centered">
            При большом числе снимков это занимает несколько секунд
          </div>
        )}

        <div className="label-key">Что внутри</div>
        <dl className="rows">
          <div className="row">
            <dt>README.txt</dt>
            <dd>описание формата по-русски</dd>
          </div>
          <div className="row">
            <dt>collection.csv</dt>
            <dd>таблица для Excel</dd>
          </div>
          <div className="row">
            <dt>collection.json</dt>
            <dd>все данные, названия строками</dd>
          </div>
          <div className="row">
            <dt>photos/</dt>
            <dd>снимки по инвентарным номерам</dd>
          </div>
        </dl>

        <div className="label-key">Копии на сервере</div>
        <p className="small muted" style={{ marginTop: 0 }}>
          Дамп базы снимается ежедневно и хранится в 30 последних копиях.
          Это другой класс защиты: дамп требует Postgres той же версии,
          а этот архив читается человеком без чего бы то ни было.
        </p>
      </div>
    </div>
  );
}
